/**
 * ProviderDetailsPage
 * Page for viewing provider details — Hero header + StatCards + SectionCards
 *
 * Importance tiers:
 *  T1 (Hero)   — TradeName, Status, CNPJ, ProviderType, Contact actions
 *  T2 (Stats)  — ContractStart, ProviderType, ClosingDate, PaymentDate
 *  T3 (Detail) — Address, Reception, Reports, Admin, Financial, Financial Contact, Dates
 *  T4 (Cond.)  — Provider Notice, Documents (only when data exists)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import { FalhaDeCarga, falhaDeCarga, textoDaFalhaDeCarga } from '../../utils/acessoNegado';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Edit,
  Phone,
  Email,
  WhatsApp,
  LocationOn,
  ArrowBack,
  Description,
  CalendarToday,
  LocalHospital,
  EventNote,
  Payment,
  Business,
  AccountBalance,
  AttachMoney,
  Warning,
  Folder,
  Download as DownloadIcon,
  Print as PrintIcon,
  Wifi as WifiIcon,
  Usb as UsbIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PageShell,
  DataTable,
  IdentityBanner,
  InfoItem,
  SectionCard,
  StatCard,
  type DataTableColumn,
} from '../../components';
import { pode } from '../../authorization/pode';
import { RouteActionButton } from '../../components/common';
import { httpClient } from '../../services/httpClient';
import {
  providerService,
  ProviderDocument,
  ProviderStatus,
  ProviderType,
  PROVIDER_DOCUMENT_TYPE_LABELS,
} from '../../services/providerService';
import type { Provider } from '../../types/provider';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { printerService } from '../../services/printerService';
import type { PrinterConfig } from '../../types/printer';
import { PRINTER_BRAND_LABELS, PrinterConnectionType } from '../../types/printer';
import PrinterConfigDialog from './components/PrinterConfigDialog';

/* ─── inline formatters ─── */

const formatCNPJ = (cnpj?: string | null): string => {
  if (!cnpj) return '—';
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const formatPhone = (phone?: string): string => {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  if (digits.length === 10) return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  return phone;
};

const formatDate = (date?: string | Date): string => {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

const formatDiaDoMes = (dia?: number | null): string => (dia ? `Dia ${dia}` : '—');

const formatCEP = (cep?: string): string => {
  if (!cep) return '—';
  const digits = cep.replace(/\D/g, '');
  if (digits.length !== 8) return cep;
  return digits.replace(/^(\d{5})(\d{3})$/, '$1-$2');
};

const calculateContractTime = (startDate?: string | Date): string | undefined => {
  if (!startDate) return undefined;
  const start = new Date(startDate);
  if (isNaN(start.getTime())) return undefined;
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  const totalMonths = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30.44));
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (years === 0) return `${months} ${months === 1 ? 'mês' : 'meses'}`;
  if (months === 0) return `${years} ${years === 1 ? 'ano' : 'anos'}`;
  return `${years}a ${months}m`;
};

/* ─── provider type labels ─── */

const PROVIDER_TYPE_LABELS: Record<ProviderType, string> = {
  [ProviderType.CLINICA]: 'Clínica',
  [ProviderType.LABORATORIO]: 'Laboratório',
  [ProviderType.HOSPITAL]: 'Hospital',
  [ProviderType.CONSULTORIO]: 'Consultório',
  [ProviderType.CENTRO_MEDICO]: 'Centro Médico',
  [ProviderType.AMBIENTAL]: 'Ambiental',
  [ProviderType.SEGURANCA]: 'Segurança',
  [ProviderType.MULTIPLO]: 'Múltiplo',
};

/* ─── status chip colors ─── */

const STATUS_CONFIG: Record<ProviderStatus, { color: 'success' | 'default' | 'warning' | 'error' | 'info'; label: string }> = {
  [ProviderStatus.ATIVO]: { color: 'success', label: 'Ativo' },
  [ProviderStatus.INATIVO]: { color: 'default', label: 'Inativo' },
  [ProviderStatus.SUSPENSO]: { color: 'warning', label: 'Suspenso' },
  [ProviderStatus.BLOQUEADO]: { color: 'error', label: 'Bloqueado' },
  [ProviderStatus.PENDENTE]: { color: 'info', label: 'Pendente' },
};

/* ══════════════════════════════════════════════════════════════ */

const ProviderDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();

  const [provider, setProvider] = useState<Provider | null>(null);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);
  const [documents, setDocuments] = useState<{
    responsibleName: string | null;
    responsibleCRM: string | null;
    documents: ProviderDocument[];
  } | null>(null);
  const [loading, setLoading] = useState(true);

  // Printer state
  const [printerDialogOpen, setPrinterDialogOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<PrinterConfig | null>(null);

  // Configurar impressora é permissão própria, e quem gere a empresa não a tem: o bloco vinha
  // vazio e calado porque a leitura era engolida por um `catch` sem corpo.
  const carregarImpressoras = useCallback(
    () => printerService.getByProvider(Number(id)),
    [id]
  );
  const impressoras = useListaPermitida<PrinterConfig>(
    'printer.config.read',
    'Impressoras',
    carregarImpressoras,
    { habilitado: Boolean(id) }
  );
  const printerConfigs = impressoras.itens;
  const loadPrinterConfigs = impressoras.recarregar;

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      providerService.getProviderById(id),
      providerService.getDocuments(id).catch(() => null),
    ])
      .then(([providerData, docsData]) => {
        setProvider(providerData);
        setDocuments(docsData);
        setFalha(null);
      })
      .catch((err: unknown) => {
        setProvider(null);
        setFalha(falhaDeCarga(err, 'Não foi possível carregar o prestador. Tente de novo.'));
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleDeletePrinter = useCallback(async (printerId: number) => {
    try {
      await printerService.delete(printerId);
      loadPrinterConfigs();
    } catch {
      // silently fail
    }
  }, [loadPrinterConfigs]);

  if (loading) {
    return (
      <PageShell
        breadcrumbs={[
          { label: 'Início', path: '/' },
          { label: 'Prestadores', path: '/providers' },
          { label: 'Detalhes' },
        ]}
        title="Detalhes do Prestador"
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!provider) {
    return (
      <PageShell
        breadcrumbs={[
          { label: 'Início', path: '/' },
          { label: 'Prestadores', path: '/providers' },
          { label: 'Detalhes' },
        ]}
        title="Detalhes do Prestador"
      >
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Prestador não encontrado.')}
          rotuloDoBotao="Voltar para os prestadores"
          aoVoltar={() => navigate('/providers')}
        />
      </PageShell>
    );
  }

  const displayName = provider.tradeName || provider.legalName;
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  const statusCfg = STATUS_CONFIG[provider.status] || STATUS_CONFIG[ProviderStatus.PENDENTE];
  const providerTypeLabel = PROVIDER_TYPE_LABELS[provider.providerType] || provider.providerType;

  const hasAddress = provider.zipCode || provider.address || provider.city;
  const hasReception = provider.receptionContactName || provider.receptionEmail || provider.receptionPhone;
  const hasReport = provider.reportContactName || provider.reportEmail || provider.reportPhone;
  const hasAdmin = provider.contactName || provider.contactEmail || provider.contactPhone;
  const hasFinancial = provider.pix || provider.bankName || provider.bankAgency || provider.bankAccount || provider.paymentMethod;
  const hasFinancialContact = provider.financialContactName || provider.financialEmail || provider.financialPhone;
  const hasDates = provider.contractStartDate || provider.contractEndDate || provider.firstContactDate || provider.terminationReason || provider.priceAdjustmentDate || provider.priceAdjustmentPercentage != null;
  const hasNotice = !!provider.providerNotice;

  const filledDocs = documents?.documents?.filter(
    (d) => d.documentNumber || d.validityDate || d.fileName
  ) || [];
  const hasDocSection = !!documents?.responsibleName || !!documents?.responsibleCRM || filledDocs.length > 0;

  const documentColumns: DataTableColumn<ProviderDocument>[] = [
    {
      id: 'documentType',
      label: 'Documento',
      primary: true,
      render: (doc) => (
        <Typography variant="body2" fontWeight={600}>
          {PROVIDER_DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType}
        </Typography>
      ),
    },
    {
      id: 'documentNumber',
      label: 'Número',
      render: (doc) => doc.documentNumber || '—',
    },
    {
      id: 'validityDate',
      label: 'Validade',
      render: (doc) => (doc.validityDate ? formatDate(doc.validityDate) : '—'),
    },
    {
      id: 'file',
      label: 'Arquivo',
      align: 'right',
      render: (doc) =>
        doc.fileName ? (
          <Tooltip title="Clique para baixar o arquivo">
            <Chip
              icon={<DownloadIcon sx={{ fontSize: 16 }} />}
              label={doc.originalName || 'Baixar arquivo'}
              size="small"
              variant="filled"
              color="primary"
              clickable
              onClick={() => {
                const url = providerService.getDocumentFileUrl(id!, doc.documentType);
                httpClient.downloadFile(url, doc.originalName || doc.fileName);
              }}
              sx={{ maxWidth: 240 }}
            />
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Prestadores', path: '/providers' },
        { label: displayName },
      ]}
      actions={
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/providers')}
          >
            Voltar
          </Button>
          <RouteActionButton
            para="/providers/:providerId/exams"
            exige={pode('provider_exam.read')}
            variant="outlined"
            size="small"
            startIcon={<ScienceIcon />}
            aoAgir={() => navigate(`/providers/${id}/exams`)}
          >
            Exames
          </RouteActionButton>
          <RouteActionButton
            para="/providers/:id/edit"
            exige={pode('provider.update')}
            variant="contained"
            size="small"
            startIcon={<Edit />}
            aoAgir={() => navigate(`/providers/${id}/edit`)}
          >
            Editar
          </RouteActionButton>
        </>
      }
    >
        {/* ═══ T1: IDENTIDADE ═══ */}
        <IdentityBanner
          avatar={initials}
          name={displayName}
          status={
            <Chip
              label={statusCfg.label}
              color={statusCfg.color}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          }
          context={
            provider.tradeName && provider.legalName && provider.tradeName !== provider.legalName
              ? provider.legalName
              : undefined
          }
          identifiers={
            <>
              <Tooltip title="Copiar CNPJ">
                <Chip
                  label={formatCNPJ(provider.cnpj)}
                  size="small"
                  variant="outlined"
                  onClick={() => navigator.clipboard.writeText(provider.cnpj ?? '')}
                  sx={{ fontWeight: 600, fontFamily: 'monospace' }}
                />
              </Tooltip>

              <Chip
                icon={<LocalHospital sx={{ fontSize: 16 }} />}
                label={providerTypeLabel}
                size="small"
                variant="outlined"
                color="primary"
              />

              {provider.receptionPhone && (
                <Tooltip title={`WhatsApp: ${formatPhone(provider.receptionPhone)}`}>
                  <IconButton
                    size="small"
                    color="success"
                    href={`https://wa.me/55${provider.receptionPhone.replace(/\D/g, '')}`}
                    target="_blank"
                    sx={{
                      bgcolor: alpha('#25D366', 0.1),
                      '&:hover': { bgcolor: alpha('#25D366', 0.2) },
                    }}
                  >
                    <WhatsApp fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {provider.receptionEmail && (
                <Tooltip title={provider.receptionEmail}>
                  <IconButton
                    size="small"
                    color="primary"
                    href={`mailto:${provider.receptionEmail}`}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                    }}
                  >
                    <Email fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          }
        />

        {/* ═══ T2: STAT CARDS ═══ */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<CalendarToday fontSize="small" />}
              label="Data de Contratação"
              value={formatDate(provider.contractStartDate)}
              sublabel={calculateContractTime(provider.contractStartDate)}
              color={theme.palette.info.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<LocalHospital fontSize="small" />}
              label="Tipo de Prestador"
              value={providerTypeLabel}
              color={theme.palette.secondary.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<EventNote fontSize="small" />}
              label="Dia do Fechamento"
              value={formatDiaDoMes(provider.closingDay)}
              sublabel={provider.closingDay ? 'de todo mês' : undefined}
              color="#8E24AA"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<Payment fontSize="small" />}
              label="Dia do Pagamento"
              value={formatDiaDoMes(provider.paymentDay)}
              sublabel={provider.paymentDay ? 'de todo mês' : undefined}
              color={theme.palette.success.main}
            />
          </Grid>
        </Grid>

        {/* ═══ T3: DETAIL SECTIONS ═══ */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {/* Address */}
          {hasAddress && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Endereço"
                icon={<LocationOn fontSize="small" />}
                accentColor={theme.palette.primary.main}
              >
                <Grid container spacing={2}>
                  {provider.zipCode && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InfoItem label="CEP" value={formatCEP(provider.zipCode)} />
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <InfoItem
                      label="Logradouro"
                      value={
                        <>
                          {provider.address || '—'}
                          {provider.addressNumber && `, ${provider.addressNumber}`}
                        </>
                      }
                    />
                  </Grid>
                  {provider.neighborhood && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InfoItem label="Bairro" value={provider.neighborhood} />
                    </Grid>
                  )}
                  {(provider.city || provider.state) && (
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <InfoItem
                        label="Cidade / UF"
                        value={[provider.city, provider.state].filter(Boolean).join(' / ')}
                      />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Reception Contact */}
          {hasReception && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Recepção"
                icon={<Phone fontSize="small" />}
                accentColor="#2E7D32"
              >
                <Grid container spacing={2}>
                  {provider.receptionContactName && (
                    <Grid size={12}>
                      <InfoItem label="Contato" value={provider.receptionContactName} />
                    </Grid>
                  )}
                  {provider.receptionEmail && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        icon={<Email sx={{ fontSize: 14 }} />}
                        label="E-mail"
                        value={provider.receptionEmail}
                      />
                    </Grid>
                  )}
                  {provider.receptionPhone && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        icon={<Phone sx={{ fontSize: 14 }} />}
                        label="Telefone"
                        value={formatPhone(provider.receptionPhone)}
                      />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Report Contact */}
          {hasReport && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Laudos"
                icon={<Description fontSize="small" />}
                accentColor="#1565C0"
              >
                <Grid container spacing={2}>
                  {provider.reportContactName && (
                    <Grid size={12}>
                      <InfoItem label="Contato" value={provider.reportContactName} />
                    </Grid>
                  )}
                  {provider.reportEmail && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        icon={<Email sx={{ fontSize: 14 }} />}
                        label="E-mail"
                        value={provider.reportEmail}
                      />
                    </Grid>
                  )}
                  {provider.reportPhone && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        icon={<Phone sx={{ fontSize: 14 }} />}
                        label="Telefone"
                        value={formatPhone(provider.reportPhone)}
                      />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Admin Contact */}
          {hasAdmin && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Administrativo"
                icon={<Business fontSize="small" />}
                accentColor="#7B1FA2"
              >
                <Grid container spacing={2}>
                  {provider.contactName && (
                    <Grid size={12}>
                      <InfoItem label="Contato" value={provider.contactName} />
                    </Grid>
                  )}
                  {provider.contactEmail && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        icon={<Email sx={{ fontSize: 14 }} />}
                        label="E-mail"
                        value={provider.contactEmail}
                      />
                    </Grid>
                  )}
                  {provider.contactPhone && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        icon={<Phone sx={{ fontSize: 14 }} />}
                        label="Telefone"
                        value={formatPhone(provider.contactPhone)}
                      />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Financial Data */}
          {hasFinancial && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Dados Financeiros"
                icon={<AccountBalance fontSize="small" />}
                accentColor="#FF8F00"
              >
                <Grid container spacing={2}>
                  {provider.pix && (
                    <Grid size={12}>
                      <InfoItem label="PIX" value={provider.pix} />
                    </Grid>
                  )}
                  {provider.bankName && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InfoItem label="Banco" value={provider.bankName} />
                    </Grid>
                  )}
                  {provider.bankAgency && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InfoItem label="Agência" value={provider.bankAgency} />
                    </Grid>
                  )}
                  {provider.bankAccount && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InfoItem label="Conta" value={provider.bankAccount} />
                    </Grid>
                  )}
                  {provider.paymentMethod && (
                    <Grid size={12}>
                      <InfoItem label="Forma de Pagamento" value={provider.paymentMethod} />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Financial Contact */}
          {hasFinancialContact && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Contato Financeiro"
                icon={<AttachMoney fontSize="small" />}
                accentColor="#E65100"
              >
                <Grid container spacing={2}>
                  {provider.financialContactName && (
                    <Grid size={12}>
                      <InfoItem label="Contato" value={provider.financialContactName} />
                    </Grid>
                  )}
                  {provider.financialEmail && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        icon={<Email sx={{ fontSize: 14 }} />}
                        label="E-mail"
                        value={provider.financialEmail}
                      />
                    </Grid>
                  )}
                  {provider.financialPhone && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        icon={<Phone sx={{ fontSize: 14 }} />}
                        label="Telefone"
                        value={formatPhone(provider.financialPhone)}
                      />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Dates & Contract */}
          {hasDates && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Datas e Contrato"
                icon={<EventNote fontSize="small" />}
                accentColor="#546E7A"
              >
                <Grid container spacing={2}>
                  {provider.contractStartDate && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Início do Contrato" value={formatDate(provider.contractStartDate)} />
                    </Grid>
                  )}
                  {provider.contractEndDate && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Fim do Contrato" value={formatDate(provider.contractEndDate)} />
                    </Grid>
                  )}
                  {provider.firstContactDate && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Primeiro Contato" value={formatDate(provider.firstContactDate)} />
                    </Grid>
                  )}
                  {provider.priceAdjustmentDate && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Data do Reajuste" value={formatDate(provider.priceAdjustmentDate)} />
                    </Grid>
                  )}
                  {provider.priceAdjustmentPercentage != null && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Reajuste de Exames" value={`${provider.priceAdjustmentPercentage}%`} />
                    </Grid>
                  )}
                  {provider.terminationReason && (
                    <Grid size={12}>
                      <InfoItem label="Motivo de Rescisão" value={provider.terminationReason} />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Thermal Printer */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Impressora Térmica"
              icon={<PrintIcon fontSize="small" />}
              accentColor="#5D4037"
            >
              {printerConfigs.length > 0 ? (
                <Box>
                  {printerConfigs.map((pc) => (
                    <Box
                      key={pc.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        mb: 1,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {pc.connectionType === PrinterConnectionType.NETWORK
                          ? <WifiIcon fontSize="small" color="success" />
                          : <UsbIcon fontSize="small" color="action" />}
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {pc.name}
                            {pc.isDefault && (
                              <Chip label="Padrão" size="small" color="primary" sx={{ ml: 1, height: 20, fontSize: 11 }} />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {PRINTER_BRAND_LABELS[pc.printerBrand]} &middot; {pc.paperWidth}
                            {pc.connectionType === PrinterConnectionType.NETWORK && pc.host && (
                              <> &middot; {pc.host}:{pc.port}</>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      <Box>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => { setEditingPrinter(pc); setPrinterDialogOpen(true); }}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover">
                          <IconButton size="small" onClick={() => handleDeletePrinter(pc.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<PrintIcon />}
                    onClick={() => { setEditingPrinter(null); setPrinterDialogOpen(true); }}
                    sx={{ mt: 1 }}
                  >
                    Adicionar impressora
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {impressoras.bloqueio ??
                      'Nenhuma impressora configurada — impressão será via navegador'}
                  </Typography>
                  {impressoras.permitido && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PrintIcon />}
                      onClick={() => { setEditingPrinter(null); setPrinterDialogOpen(true); }}
                    >
                      Configurar Impressora
                    </Button>
                  )}
                </Box>
              )}
            </SectionCard>
          </Grid>
        </Grid>

        {/* ═══ T4: CONDITIONAL SECTIONS ═══ */}
        {(hasNotice || hasDocSection) && (
          <Grid container spacing={2.5}>
            {/* Provider Notice */}
            {hasNotice && (
              <Grid size={12}>
                <SectionCard
                  title="Aviso do Prestador"
                  icon={<Warning fontSize="small" />}
                  accentColor="#D32F2F"
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: alpha('#D32F2F', 0.04),
                      borderColor: alpha('#D32F2F', 0.2),
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} color="error.dark">
                      {provider.providerNotice}
                    </Typography>
                  </Paper>
                </SectionCard>
              </Grid>
            )}

            {/* Documents */}
            {hasDocSection && (
              <Grid size={12}>
                <SectionCard
                  title="Documentos e Certificados"
                  icon={<Folder fontSize="small" />}
                  accentColor="#00695C"
                >
                  {(documents?.responsibleName || documents?.responsibleCRM) && (
                    <Grid container spacing={2} sx={{ mb: filledDocs.length > 0 ? 2 : 0 }}>
                      {documents?.responsibleName && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <InfoItem label="Responsável Técnico" value={documents.responsibleName} />
                        </Grid>
                      )}
                      {documents?.responsibleCRM && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <InfoItem label="CRM" value={documents.responsibleCRM} />
                        </Grid>
                      )}
                    </Grid>
                  )}

                  {filledDocs.length > 0 && (
                    <DataTable
                      columns={documentColumns}
                      rows={filledDocs}
                      getRowId={(doc) => doc.documentType}
                      emptyState={{
                        title: 'Nenhum documento preenchido para este prestador',
                        description: 'Os documentos e certificados vêm da edição do cadastro deste prestador.',
                      }}
                    />
                  )}
                </SectionCard>
              </Grid>
            )}
          </Grid>
        )}
        {/* Printer Config Dialog */}
        <PrinterConfigDialog
          open={printerDialogOpen}
          onClose={() => { setPrinterDialogOpen(false); setEditingPrinter(null); }}
          onSaved={loadPrinterConfigs}
          providerId={Number(id)}
          config={editingPrinter}
        />
    </PageShell>
  );
};

export default ProviderDetailsPage;
